import React, { useCallback, useContext } from 'react'
import { Button } from '@/components/ui/button'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSub,
  DropdownMenuSubContent,
  DropdownMenuSubTrigger,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import { GridColumn as Column, Grid, GridCellProps } from '@progress/kendo-react-grid'
import { ClientDetailedDto } from 'api/Clients/Types/ClientDetailedDto'
import { TestDto } from 'api/Clients/Types/TestDto'
import {
  clientTestTypes,
  getTestSubcategoryByName,
  TestSubcategory,
} from 'api/Clients/Types/TestQuestionDto'
import { TickmillCompanyTypes } from 'api/Clients/Types/TickmillCompanyTypes'
import { PermissionsEnum } from 'api/Identity/Types/PermissionsEnum'
import { FlexBox } from 'components/Box'
import MoreButton from 'components/MoreButton/MoreButton'
import { successToast, ToastContext } from 'components/Toast/Toast'
import { MoreIcon } from 'icons/MoreIcon'
import { ChevronDown } from 'lucide-react'
import styled from 'styled-components'
import { useApiClient } from 'utils/api/ApiClient'
import { ClientAreaApiClient } from 'utils/api/clientAreaApi'
import { ClientsApiClient } from 'utils/api/clientsApi'
import { useCallbackWithForceRefresh } from 'utils/api/useCallbackWithForceRefresh'
import { useFetchOne } from 'utils/api/useFetch'
import { AuthSessionContext } from 'utils/AuthContext'
import { formatLocalDateUTC } from 'utils/date'
import { handleOpenWindow } from 'utils/openTestWindow'
import { hasPermission } from 'utils/permissionsValidator'
import stringComparator from 'utils/stringComparator'
import { TenantFeature } from 'utils/Tenant'

const IconButton = styled.button`
  margin: 0;
  border: 0;
  display: flex;
  background-color: transparent;
  cursor: pointer;
  fill: #29377b;

  svg {
    width: 24px;
    height: 24px;
  }

  &:focus {
    outline: 0;
    border-color: #29377b;
  }
`

interface Props {
  data: ClientDetailedDto
  forceRefresh(): void
}

const ClientClassificationTestPage: React.FC<Props> = ({ data, forceRefresh }) => {
  const clientAreaApiClient = useApiClient(ClientAreaApiClient)
  const clientsApiClient = useApiClient(ClientsApiClient)

  const [auth] = useContext(AuthSessionContext)
  const permissions = auth?.permissions || []

  const { callback: clientTests, forceRefresh: forceRefreshTests } = useCallbackWithForceRefresh(
    () => clientsApiClient.getClientClassificationAnswers(data.id),
    [data.id]
  )

  const classificationTestOptionsCallback = useCallback(async () => {
    return clientsApiClient.getClassificationTestsOptions()
  }, []) // eslint-disable-line react-hooks/exhaustive-deps

  const { data: testOptions } = useFetchOne(classificationTestOptionsCallback)

  const { data: tests } = useFetchOne(clientTests)
  const setToast = useContext(ToastContext)

  const updateTestStatus = async (testId: string, statusId: number) => {
    await clientsApiClient.setClientTestStatus(testId, statusId)
    setToast(successToast('Classification Status updated successfully'))
    forceRefreshTests()
    forceRefresh()
  }

  const sendAutomatedEmail = async (templateId: string) => {
    await clientAreaApiClient.postClassificationClientTestEmail(data.id, templateId)
    setToast(successToast('Email sent successfully'))
  }

  const handleOpenFilledTest = ({ id, test }: TestDto) => {
    handleOpenWindow({
      urlKey: TenantFeature.clientTestFilledWindow,
      pathParams: { testId: id ?? '', clientId: data.id, type: clientTestTypes.classification },
      additionalParams: { subcategoryId: getTestSubcategoryByName(test.subcategory).toString() },
    })
  }

  const handleEditTestForm = ({ id, test }: TestDto) => {
    handleOpenWindow({
      permissions,
      permission: PermissionsEnum.CanAddClientClassification,
      urlKey: TenantFeature.clientEditTestFormWindow,
      pathParams: { testId: id ?? '', clientId: data.id, type: clientTestTypes.classification },
      additionalParams: { subcategoryId: getTestSubcategoryByName(test.subcategory).toString() },
    })
  }

  const handleAddNewTest = ({
    subcategoryId,
    tickmillCompanyId,
  }: {
    subcategoryId: number
    tickmillCompanyId: number
  }) => {
    handleOpenWindow({
      permissions,
      permission: PermissionsEnum.CanAddClientClassification,
      urlKey: TenantFeature.clientNewTestFormWindow,
      pathParams: { tickmillCompanyId, clientId: data.id, type: clientTestTypes.classification },
      additionalParams: { subcategoryId: subcategoryId.toString() },
    })
  }

  const requestsSubMenu = () => (
    <DropdownMenuSub key='requests'>
      <DropdownMenuSubTrigger>Requests</DropdownMenuSubTrigger>
      <DropdownMenuSubContent>
        {(testOptions || [])
          .sort((a, b) => stringComparator(b.id, a.id))
          .map((option) => (
            <DropdownMenuItem
              key={option.name}
              onSelect={() => sendAutomatedEmail(option.id.toString())}
            >
              {option.name}
            </DropdownMenuItem>
          ))}
      </DropdownMenuSubContent>
    </DropdownMenuSub>
  )

  const renderMenu = () => {
    return (
      <>
        <DropdownMenuSub>
          <DropdownMenuSubTrigger>Add new</DropdownMenuSubTrigger>
          <DropdownMenuSubContent>
            <DropdownMenuItem
              onSelect={() =>
                handleAddNewTest({
                  tickmillCompanyId: data.tickmillCompany.id,
                  subcategoryId: TestSubcategory.ClassificationProfessional,
                })
              }
            >
              Professional
            </DropdownMenuItem>
            {data.tickmillCompany.id === TickmillCompanyTypes.Tickmill_EU_Company && (
              <DropdownMenuItem
                onSelect={() =>
                  handleAddNewTest({
                    tickmillCompanyId: data.tickmillCompany.id,
                    subcategoryId: TestSubcategory.ClassificationExperiencedRetail,
                  })
                }
              >
                Polish Experienced Retail
              </DropdownMenuItem>
            )}
          </DropdownMenuSubContent>
        </DropdownMenuSub>
        {requestsSubMenu()}
      </>
    )
  }

  const renderOutcomeMenu = (dataItem: TestDto) => {
    return (
      <>
        <DropdownMenuItem onSelect={() => updateTestStatus(dataItem.id, 2)}>
          Approve
        </DropdownMenuItem>
        <DropdownMenuItem onSelect={() => updateTestStatus(dataItem.id, 3)}>
          Decline
        </DropdownMenuItem>
      </>
    )
  }

  return (
    <React.Fragment>
      <FlexBox style={{ marginBottom: 36 }} alignItems='center' justifyContent='flex-start'>
        <DropdownMenu>
          <DropdownMenuTrigger
            render={
              <Button
                type='button'
                variant='outline'
                disabled={!hasPermission(permissions, PermissionsEnum.CanAddClientClassification)}
              />
            }
          >
            <>
              Options <ChevronDown />
            </>
          </DropdownMenuTrigger>
          <DropdownMenuContent align='start'>{renderMenu()}</DropdownMenuContent>
        </DropdownMenu>
      </FlexBox>
      <Grid data={tests || []} resizable>
        <Column
          field='test.name'
          title='Name'
          className='table-columns__content'
          cell={(gridCellProps: GridCellProps) => (
            <td
              className='table-columns__link'
              onClick={() => handleOpenFilledTest(gridCellProps.dataItem)}
            >
              {gridCellProps.dataItem.test.name}
            </td>
          )}
        />
        <Column title='User' field='user.name' className='table-columns__content' />
        <Column title='Status' field='testStatus.name' className='table-columns__content' />
        <Column
          field='dateTaken'
          title='Date Submitted'
          cell={(gridCellProps: GridCellProps) => (
            <td className='table-columns__date'>
              {formatLocalDateUTC(gridCellProps.dataItem.dateTaken, true)}
            </td>
          )}
        />
        <Column
          title='Retake Allowed'
          cell={(props: GridCellProps) => (
            <td className='table-columns__content'>
              <input type='checkbox' checked={props.dataItem.allowRetake} disabled />
            </td>
          )}
        />
        <Column
          title='Outcome'
          cell={(gridCellProps: GridCellProps) => (
            <td>
              <DropdownMenu>
                <DropdownMenuTrigger
                  render={
                    <IconButton
                      type='button'
                      aria-label='More options'
                      disabled={
                        !hasPermission(permissions, PermissionsEnum.CanChangeClientTestStatus) ||
                        (gridCellProps.dataItem.testStatus || {}).id !== 1
                      }
                    />
                  }
                >
                  <MoreIcon />
                </DropdownMenuTrigger>
                <DropdownMenuContent align='end'>
                  {renderOutcomeMenu(gridCellProps.dataItem)}
                </DropdownMenuContent>
              </DropdownMenu>
            </td>
          )}
        />
        {hasPermission(permissions, PermissionsEnum.CanAddClientClassification) && (
          <Column
            width={50}
            title=''
            cell={(gridCellProps: GridCellProps) => (
              <td>
                <MoreButton
                  items={[
                    {
                      name: 'Edit',
                      'data-test': 'edit',
                      onClick: () => handleEditTestForm(gridCellProps.dataItem),
                    },
                  ]}
                  data-test='more-button'
                />
              </td>
            )}
          />
        )}
      </Grid>
    </React.Fragment>
  )
}

export default ClientClassificationTestPage
